import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { activitySnapshot, beginActivity, subscribeActivity } from '../activity.js';

export function useActivity(active, label, { message = '', progress = null, cancel, delay = 300 } = {}) {
  const job = useRef(null);
  useEffect(() => {
    if (!active) return;
    const current = beginActivity(label, { delay }); job.current = current;
    return () => { current.finish(); job.current = null; };
  }, [active, label, delay]);
  useEffect(() => { job.current?.update({ message, progress, cancel }); }, [active, label, message, progress, cancel, delay]);
}

export function ElapsedTime({ started }) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => { setNow(Date.now()); const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, [started]);
  const seconds = Math.max(0, Math.floor((now - started) / 1000));
  return <span className="activity-elapsed">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')} elapsed</span>;
}

export function ProgressBar({ value, label }) {
  const known = Number.isFinite(value), percent = known ? Math.round(value * 100) : undefined;
  return <div className={'activity-progress' + (known ? '' : ' indeterminate')} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
    <i style={known ? { width: percent + '%' } : undefined} />
  </div>;
}

export default function ActivityStatus({ embedded = false }) {
  const jobs = useSyncExternalStore(subscribeActivity, activitySnapshot, activitySnapshot);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (!jobs.length) setExpanded(false); }, [jobs.length]);
  if (!jobs.length) return null;
  const current = jobs.at(-1);
  return <aside className={'activity-status ' + (embedded ? 'embedded' : 'global')} aria-label="App activity">
    <div className="activity-summary">
      <span className="spinner" aria-hidden="true" />
      <span className="activity-label" role="status">{current.label}</span>
      <ElapsedTime started={current.started} />
      <button className="btn sm" aria-expanded={expanded} onClick={() => setExpanded(v => !v)}>{expanded ? 'Hide activity' : jobs.length > 1 ? `${jobs.length} activities` : 'Details'}</button>
    </div>
    <ProgressBar value={current.progress} label={current.label} />
    {expanded && <ul className="activity-list">{jobs.map(job => <li key={job.id}>
      <div><b>{job.label}</b><ElapsedTime started={job.started} />{job.cancel && <button className="btn sm" onClick={job.cancel}>Cancel</button>}</div>
      {job.message && <p>{job.message}</p>}
      <ProgressBar value={job.progress} label={job.label + ' details'} />
    </li>)}</ul>}
  </aside>;
}
